import React, { useState, useEffect, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia, alertErpia } from './ConfirmDialog';
import { Plus, X, Trash2, Database, ChevronDown, Search, Filter, Upload, Loader, FileText, Send, Settings } from 'lucide-react';
import APIService from '../services/api';
import CanonicalDocumentViewer from './DocumentViewer';
import { ancre } from '../tutoriel/ancres';
import BoutonAnnonce from './annonces/BoutonAnnonce';
import AnnonceTiroir from './annonces/AnnonceTiroir';

// ─── Mapping classe Tailwind → hex d'accent (les colonnes stockent des classes bg-*) ──
const TW_HEX = {
  'bg-slate-500': '#64748b', 'bg-slate-600': '#475569',
  'bg-blue-400': '#60a5fa', 'bg-blue-500': '#3b82f6', 'bg-blue-600': '#2563eb',
  'bg-indigo-500': '#6366f1', 'bg-indigo-600': '#4f46e5',
  'bg-violet-500': '#8b5cf6', 'bg-violet-600': '#7c3aed',
  'bg-purple-500': '#a855f7', 'bg-purple-600': '#9333ea',
  'bg-amber-500': '#f59e0b', 'bg-amber-600': '#d97706',
  'bg-orange-500': '#f97316', 'bg-orange-600': '#ea580c',
  'bg-green-500': '#22c55e', 'bg-green-600': '#16a34a',
  'bg-red-400': '#f87171', 'bg-red-500': '#ef4444',
  'bg-rose-500': '#f43f5e', 'bg-rose-600': '#e11d48',
  'bg-pink-500': '#ec4899',
  'bg-teal-500': '#14b8a6', 'bg-teal-600': '#0d9488',
  'bg-cyan-500': '#06b6d4', 'bg-cyan-600': '#0891b2',
};
const accentHex = (twClass) => TW_HEX[twClass] || '#64748b';

// helper exact du handoff : dérive un rgba depuis un hex
const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
// thème : on lit data-theme pour ajuster les alphas (priorité clair)
const isDark = () => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');

// styles en-tête de colonne — valeurs EXACTES du handoff
const colHeadStyle = (accent) => {
  const dark = isDark();
  return { display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 7px 9px 12px',
    background: hexA(accent, dark ? 0.13 : 0.085), border: '1px solid ' + hexA(accent, dark ? 0.32 : 0.24), borderRadius: '9px' };
};
const colCountStyle = (accent) => {
  const dark = isDark();
  return { fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: accent,
    background: hexA(accent, dark ? 0.24 : 0.16), padding: '1px 7px', borderRadius: '6px', flex: 'none' };
};

// ─── Styles Swiss du Vivier (scopés via préfixe vrh-) ────────────────────────
const VRH_STYLES = `
.vrh-root { display:flex; flex-direction:column; background:var(--bg); font-family:'IBM Plex Sans',system-ui,sans-serif; }
.vrh-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 24px; background:var(--surface); border-bottom:1px solid var(--border); flex-shrink:0; flex-wrap:wrap; }
.vrh-toolbar-main { display:flex; align-items:center; gap:12px; min-width:0; }
.vrh-toolbar-actions { display:flex; align-items:center; gap:8px; margin-left:auto; }
.vrh-summary { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.vrh-summary-item { display:inline-flex; align-items:baseline; gap:5px; padding:4px 8px; border:1px solid var(--border); border-radius:7px; background:var(--surface); }
.vrh-summary-value { font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:700; color:var(--text-strong); }
.vrh-summary-label { font-size:10px; color:var(--text-3); }
.vrh-addcol { display:flex; align-items:center; gap:12px; padding:12px 24px; background:var(--surface); border-bottom:1px solid var(--hair); }
.vrh-input { padding:8px 14px; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:9px; font-size:13px; font-family:inherit; outline:none; }
.vrh-input:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
.vrh-btn-ghost { display:flex; align-items:center; gap:7px; padding:8px 14px; border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text); border-radius:8px; font-size:13px; font-weight:500; font-family:inherit; cursor:pointer; white-space:nowrap; }
.vrh-btn-ghost:hover { background:var(--ghost-bg-hover); }
.vrh-btn-primary { display:flex; align-items:center; gap:7px; padding:8px 16px; border:none; background:var(--accent); color:var(--accent-fg); border-radius:8px; font-size:13px; font-weight:600; font-family:inherit; cursor:pointer; white-space:nowrap; }
.vrh-col { flex-shrink:0; width:300px; display:flex; flex-direction:column; gap:8px; transition:opacity .18s ease, transform .18s ease; }
.vrh-col-drop-target { transform:translateY(-2px); }
.vrh-col-name { font-size:13px; font-weight:700; color:var(--text-strong); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:pointer; }
.vrh-col-sq { width:8px; height:8px; border-radius:2px; flex-shrink:0; }
.vrh-col-btn { background:transparent; border:none; color:var(--text-2); width:24px; height:24px; display:flex; align-items:center; justify-content:center; border-radius:6px; cursor:pointer; flex:none; }
.vrh-col-btn:hover { background:var(--ghost-bg-hover); color:var(--text); }
/* Le nom entier, sur deux lignes au plus (14.10 : « le titre est tronqué ») —
   « Développeur Quadient Inspire » ne se lit pas « Développ… ». */
.vrh-col-titre { font-size:13px; font-weight:600; line-height:1.25; color:var(--text-strong); cursor:pointer;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; overflow-wrap:anywhere; }
.vrh-col-titre:hover { text-decoration:underline; }
.vrh-col-btn--attente { position:relative; color:var(--erp-ink-strong, var(--text)); }
.vrh-col-btn--attente::after { content:''; position:absolute; top:3px; right:3px; width:6px; height:6px; border-radius:50%; background:var(--erp-warning); }
.vrh-cards { display:flex; flex-direction:column; gap:8px; min-height:8px; padding:2px 2px 10px; }
.vrh-empty { min-height:72px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; border:1.5px dashed var(--border); border-radius:10px; padding:14px; text-align:center; font-size:12px; color:var(--text-faint); transition:all .14s ease; }
.vrh-empty-action { border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text-2); border-radius:7px; padding:5px 9px; font:600 11px 'IBM Plex Sans',system-ui,sans-serif; cursor:pointer; }
.vrh-empty-action:hover { background:var(--ghost-bg-hover); color:var(--text); }
.vrh-card { background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:14px; cursor:pointer; transition:transform .15s ease, border-color .15s ease, box-shadow .15s ease; user-select:none; overflow:visible; animation:vrhCardIn .24s ease both; animation-delay:var(--vrh-stagger,0ms); }
.vrh-card:hover { border-color:var(--row-hover-bd); transform:translateY(-1px); box-shadow:0 4px 16px var(--hover-shadow); }
.vrh-card:focus-visible { outline:2px solid var(--accent); outline-offset:2px; }
.vrh-card-drag { opacity:.42; transform:rotate(2deg) scale(.985); }
.vrh-avatar { width:38px; height:38px; border-radius:9px; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:var(--accent-soft-bg); color:var(--text-strong); font-size:13px; font-weight:500; font-family:'IBM Plex Mono',monospace; }
.vrh-name { font-size:14px; font-weight:600; color:var(--text-strong); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.vrh-title { font-size:12.5px; color:var(--text-2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.vrh-name:hover, .vrh-title:hover { text-decoration:underline; text-decoration-thickness:1px; text-underline-offset:2px; }
.vrh-info { margin-top:11px; display:flex; flex-direction:column; gap:7px; }
.vrh-meta-row { display:flex; align-items:center; gap:14px; font-size:12px; color:var(--text-2); }
.vrh-meta-row .it { display:inline-flex; align-items:center; gap:5px; }
.vrh-dispo { display:inline-flex; align-items:center; gap:6px; font-size:12.5px; font-weight:600; color:#16a34a; }
.vrh-chips { display:flex; flex-wrap:wrap; gap:5px; }
.vrh-chip { font-size:11.5px; padding:2px 9px; border-radius:6px; background:var(--chip-bg); color:var(--chip-fg); }
.vrh-note { margin:0; font-size:12px; color:var(--text-2); background:var(--code-bg); border-radius:8px; padding:8px 11px; line-height:1.45; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.vrh-line { margin:0; font-size:12px; color:var(--text-2); display:flex; align-items:center; gap:6px; }
.vrh-link { background:none; border:none; padding:0; font-size:12px; color:var(--text); font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.vrh-link:hover { color:var(--text-strong); }
.vrh-foot { display:flex; align-items:center; justify-content:space-between; margin-top:4px; font-size:11px; color:var(--text-faint); }
.vrh-wa { font-size:10px; background:#25d366; color:#fff; padding:1px 7px; border-radius:5px; text-decoration:none; font-weight:600; }
.vrm-overlay { position:fixed; inset:0; background:rgba(4,6,11,0.55); backdrop-filter:blur(2px); display:flex; align-items:center; justify-content:center; z-index:300; padding:24px; }
.vrm-modal { background:var(--surface); border:1px solid var(--border); border-radius:14px; box-shadow:0 24px 60px rgba(0,0,0,.25); width:100%; max-width:560px; display:flex; flex-direction:column; max-height:90vh; font-family:'IBM Plex Sans',system-ui,sans-serif; }
.vrm-head { display:flex; align-items:center; justify-content:space-between; padding:16px 22px; border-bottom:1px solid var(--hair); flex-shrink:0; }
.vrm-title { margin:0; font-size:17px; font-weight:700; color:var(--text-strong); }
.vrm-x { background:transparent; border:none; width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; color:var(--text-2); cursor:pointer; }
.vrm-x:hover { background:var(--ghost-bg-hover); color:var(--text); }
.vrm-body { padding:18px 22px; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:14px; }
.vrm-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.vrm-field { display:flex; flex-direction:column; gap:6px; }
.vrm-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-3); }
.vrm-input { width:100%; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:9px; padding:9px 12px; font-size:13.5px; font-family:inherit; outline:none; box-sizing:border-box; }
.vrm-input:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
textarea.vrm-input { resize:vertical; min-height:72px; }
.vrm-foot { display:flex; gap:10px; padding:16px 22px; border-top:1px solid var(--hair); flex-shrink:0; }
.vrm-btn-primary { flex:1; padding:11px; border:none; background:var(--accent); color:var(--accent-fg); border-radius:9px; font-size:13.5px; font-weight:600; font-family:inherit; cursor:pointer; }
.vrm-btn-primary:disabled { opacity:.4; cursor:not-allowed; }
.vrm-btn-ghost { padding:11px 18px; border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text); border-radius:9px; font-size:13.5px; font-weight:500; font-family:inherit; cursor:pointer; }
.vrm-btn-ghost:hover { background:var(--ghost-bg-hover); }
.vrm-wa-btn { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#fff; background:#25d366; padding:6px 12px; border-radius:8px; width:fit-content; text-decoration:none; font-weight:600; }
.vrm-cv-chip { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:600; padding:6px 11px; border-radius:8px; cursor:pointer; border:1px solid; background:transparent; font-family:inherit; }
.vrm-cv-ok { color:#16a34a; border-color:rgba(22,163,74,0.3); }
.vrm-cv-attach { color:var(--text); border-color:var(--input-border); }
.vrm-cv-attach:hover { background:var(--ghost-bg-hover); }
@keyframes vrhFade { from { opacity:0; } to { opacity:1; } }
@keyframes vrhSlide { from { transform:translateX(28px); opacity:0; } to { transform:translateX(0); opacity:1; } }
@keyframes vrhCardIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
.vrh-loading-wrap { display:grid; grid-template-columns:repeat(5,minmax(184px,240px)); gap:10px; padding:12px; overflow:hidden; }
.vrh-loading-col { display:flex; flex-direction:column; gap:8px; }
.vrh-skeleton { height:138px; border-radius:9px; background:linear-gradient(100deg,var(--surface) 30%,var(--ghost-bg-hover) 45%,var(--surface) 60%); background-size:200% 100%; border:1px solid var(--border); animation:vrhShimmer 1.3s ease-in-out infinite; }
.vrh-skeleton-head { height:38px; }
@keyframes vrhShimmer { to { background-position:-200% 0; } }
@media (prefers-reduced-motion: reduce) { .vrh-card, .vrh-skeleton, .vrh-col, .vd-overlay, .vd-panel { animation:none; transition:none; } }
.vd-overlay { position:fixed; inset:0; background:rgba(4,6,11,0.55); backdrop-filter:blur(2px); z-index:300; animation:vrhFade .16s ease; }
.vd-panel { position:absolute; top:0; right:0; height:100%; width:472px; max-width:92vw; background:var(--panel); border-left:1px solid var(--border); box-shadow:-16px 0 50px rgba(0,0,0,0.35); display:flex; flex-direction:column; animation:vrhSlide .24s cubic-bezier(.22,.61,.36,1); font-family:'IBM Plex Sans',system-ui,sans-serif; }
.vd-head { display:flex; align-items:flex-start; gap:14px; padding:22px 22px 18px; }
.vd-av { width:54px; height:54px; border-radius:10px; flex:none; display:flex; align-items:center; justify-content:center; background:var(--accent-soft-bg); color:var(--text-strong); font-size:18px; font-weight:500; font-family:'IBM Plex Mono',monospace; }
.vd-name { margin:0; font-size:19px; font-weight:700; color:var(--text-strong); line-height:1.2; }
.vd-sub { margin:3px 0 0; font-size:13.5px; color:var(--text-2); }
.vd-x { background:transparent; border:none; width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; color:var(--text-2); cursor:pointer; flex:none; }
.vd-x:hover { background:var(--ghost-bg-hover); color:var(--text); }
.vd-body { flex:1; overflow-y:auto; }
.vd-sect { padding:16px 22px; border-top:1px solid var(--hair); }
.vd-actions { display:flex; flex-wrap:wrap; gap:8px; padding:0 22px 18px; }
.vd-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 15px; border-radius:8px; font-size:13px; font-weight:600; font-family:inherit; cursor:pointer; border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text); text-decoration:none; }
.vd-btn:hover { background:var(--ghost-bg-hover); }
.vd-btn-primary { background:var(--accent); color:var(--accent-fg); border:none; }
.vd-btn-primary:hover { background:var(--accent-press); color:var(--accent-fg); }
.vd-btn-wa { background:#22a557; color:#fff; border:none; }
.vd-btn-wa:hover { background:#1c8f4a; color:#fff; }
.vd-dispo-row { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.vd-dispo { display:inline-flex; align-items:center; gap:7px; font-size:13.5px; font-weight:600; color:#16794a; }
.vd-colchip { font-size:11.5px; font-weight:600; padding:3px 10px; border-radius:7px; background:var(--accent-soft-bg); color:var(--accent-soft-fg); }
.vd-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px 18px; }
.vd-k { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-3); margin:0 0 3px; }
.vd-v { font-size:13.5px; font-weight:500; color:var(--text); margin:0; }
.vd-seclabel { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-3); margin:0 0 9px; }
.vd-note { background:var(--ghost-bg-hover); border-radius:9px; padding:12px 14px; font-size:13px; line-height:1.55; color:var(--text); }
.vd-tag { font-size:11.5px; font-weight:600; padding:3px 10px; border-radius:7px; background:var(--accent-soft-bg); color:var(--accent-soft-fg); }
.vd-wa-badge { font-size:10px; background:#25d366; color:#fff; padding:1px 7px; border-radius:5px; font-weight:600; margin-left:6px; }
.vd-foot { display:flex; gap:10px; padding:16px 22px; border-top:1px solid var(--border); flex-shrink:0; }
    /* Refonte board — même rythme visuel que Pipeline, sans perdre les actions métier. */
    .vrh-toolbar{min-height:54px;padding:10px 16px;background:var(--header-bg);border-bottom-color:var(--border)}
    .vrh-toolbar-main{gap:9px}.vrh-summary{gap:4px}.vrh-summary-item{padding:3px 6px}.vrh-summary-label{font-size:9px}.vrh-toolbar-actions{margin-left:auto}
    .vrh-toolbar h1{font-size:16px!important;letter-spacing:-.025em!important}.vrh-toolbar p{font-size:10.5px!important;color:var(--text-3)!important}
    .vrh-board-wrap{overflow:auto;flex:1;min-height:0;background:var(--bg);scrollbar-color:var(--border) transparent}.vrh-board{display:flex;gap:10px;align-items:flex-start;min-width:max-content;padding:12px}
    .vrh-col{width:clamp(184px,16vw,240px);gap:7px}.vrh-col>div[draggable]{min-height:38px}.vrh-col-name{font-size:11.5px;letter-spacing:-.01em}.vrh-col-sq{width:7px;height:7px}.vrh-col-btn{width:22px;height:22px}
    .vrh-cards{gap:7px}.vrh-card{padding:11px 11px 10px;border-radius:9px}.vrh-card:hover{box-shadow:0 7px 18px var(--hover-shadow)}.vrh-avatar{width:34px;height:34px;border-radius:8px;font-size:11px}.vrh-name{font-size:12.5px}.vrh-title{font-size:11px}.vrh-info{margin-top:9px;gap:6px}.vrh-meta-row{gap:9px;font-size:10.5px}.vrh-dispo{font-size:10.5px}.vrh-chips{gap:4px}.vrh-chip{font-size:9.5px;padding:2px 6px}.vrh-note{font-size:10.5px;padding:7px 8px;line-height:1.4}.vrh-line{font-size:10.5px}.vrh-foot{font-size:9.5px}.vrh-empty{padding:15px 10px;font-size:10.5px}
    @media(max-width:760px){.vrh-toolbar{align-items:stretch;flex-direction:column}.vrh-toolbar-main,.vrh-toolbar-actions{width:100%;margin-left:0}.vrh-toolbar-main{align-items:flex-start}.vrh-toolbar-actions{display:grid;grid-template-columns:1fr 1fr;gap:6px}.vrh-toolbar-actions>.relative{grid-column:span 2}.vrh-toolbar .vrh-input{width:100%!important}.vrh-board{padding:10px}.vrh-col{width:196px}}
    
\n/* Aperçu candidat — qualification rapide sans quitter le vivier. */\n.vrh-card-selected{border-color:var(--accent)!important;box-shadow:0 0 0 2px var(--accent-soft-bg),0 7px 18px var(--hover-shadow)!important}.vd-panel{width:382px;max-width:94vw}.vd-preview-kicker{display:flex;align-items:center;justify-content:space-between;padding:16px 22px 0;color:var(--text-3);font:700 9.5px 'IBM Plex Mono',monospace;letter-spacing:.1em}.vd-preview-kicker strong{color:var(--accent-soft-fg);font-size:9px}.vd-head{padding-top:13px}.vd-match{display:inline-flex;align-items:center;gap:4px;margin-top:7px;color:var(--accent-soft-fg);font:700 10px 'IBM Plex Mono',monospace;text-transform:uppercase}.vd-btn-shortlist{flex:1;justify-content:center}.vd-btn-shortlist.is-active{border-color:var(--accent);background:var(--accent-soft-bg);color:var(--accent-soft-fg)}.vd-status-section{padding-top:14px}.vd-status-card{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 12px;border:1px solid var(--border);border-radius:9px;background:var(--ghost-bg)}.vd-status-card strong{color:var(--text-strong);font-size:14px}.vd-status-card span{display:inline-flex;align-items:center;gap:5px;color:var(--success-fg);font-size:11px;font-weight:600}.vd-status-card span i{font-size:6px}.vd-status-section .vd-colchip{display:inline-flex;align-items:center;gap:5px;margin-top:9px}.vd-contact-list{display:flex;flex-direction:column;gap:8px}.vd-contact-link{display:flex;align-items:center;gap:8px;overflow:hidden;color:var(--text-2);font-size:12px;text-decoration:none;text-overflow:ellipsis;white-space:nowrap}.vd-contact-link:hover{color:var(--text-strong)}.vd-contact-link i{width:16px;color:var(--text-3);text-align:center}.vd-tags{display:flex;flex-wrap:wrap;gap:5px}.vd-activity{display:flex;align-items:flex-start;gap:9px;padding:10px 11px;border-radius:9px;background:var(--ghost-bg)}.vd-activity-dot{width:7px;height:7px;margin-top:5px;flex:none;border-radius:50%;background:var(--success)}.vd-activity div{display:flex;min-width:0;flex-direction:column;gap:3px}.vd-activity strong{color:var(--text-2);font-size:12px;font-weight:500;line-height:1.45}.vd-activity small{color:var(--text-faint);font:10px 'IBM Plex Mono',monospace}.vd-info-line{display:flex;align-items:flex-start;gap:8px}.vd-info-line>i{width:15px;margin-top:2px;color:var(--text-3);text-align:center}.vd-navigation{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:9px 22px;border-top:1px solid var(--hair);color:var(--text-3);font-size:10.5px}.vd-navigation button{display:grid;place-items:center;width:28px;height:28px;border:1px solid var(--input-border);border-radius:7px;background:var(--ghost-bg);color:var(--text-2);cursor:pointer}.vd-navigation button:hover:not(:disabled){background:var(--ghost-bg-hover);color:var(--text-strong)}.vd-navigation button:disabled{cursor:not-allowed;opacity:.35}.vd-foot{display:flex;flex-direction:column;gap:9px;padding:12px 22px 16px}.vd-foot-primary{display:flex;gap:8px}.vd-foot-primary .vd-btn-primary{flex:1;justify-content:center}.vd-foot-secondary{display:flex;align-items:center;justify-content:space-between;gap:8px}.vd-text-action{padding:0;border:0;background:transparent;color:var(--text-3);font:600 10.5px 'IBM Plex Sans',sans-serif;cursor:pointer}.vd-text-action:hover{color:var(--text-strong);text-decoration:underline}.vd-foot .vd-btn{padding:8px 10px;font-size:11.5px}\n@media(max-width:600px){.vd-panel{width:100%;max-width:100%}.vd-head,.vd-preview-kicker,.vd-actions,.vd-sect,.vd-navigation,.vd-foot{padding-left:16px;padding-right:16px}.vd-foot-secondary{flex-wrap:wrap}.vd-text-action{font-size:10px}}\n`;

const BASE = 'https://besoins-tat-production.up.railway.app';

const DEFAULT_COLONNES = [
  { id: 'ba_bancaire',      label: 'BA Bancaire',           color: 'bg-blue-600' },
  { id: 'pm_bancaire',      label: 'PM Bancaire',           color: 'bg-indigo-600' },
  { id: 'ba_public',        label: 'BA Secteur Public',     color: 'bg-violet-600' },
  { id: 'pm_public',        label: 'PM Secteur Public',     color: 'bg-purple-600' },
  { id: 'ing_applicatif',   label: 'Ingénieurs Applicatifs',color: 'bg-cyan-600' },
  { id: 'coup_de_coeur',    label: '❤️ Coup de cœur',      color: 'bg-rose-500' },
];

// ─── Carte candidat vivier ───────────────────────────────────────────────────
// Édition inline du label de colonne (double-clic)
function ColLabelEditor({ label, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(label);
  const commit = () => {
    if (val.trim()) onSave(val.trim());
    setEditing(false);
  };
  if (editing) return (
    <input autoFocus value={val} onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(label); setEditing(false); } }}
      onClick={e => e.stopPropagation()}
      className="bg-white bg-opacity-20 text-white placeholder-white font-semibold text-sm rounded px-1 py-0.5 outline-none w-32 border border-white border-opacity-40"
    />
  );
  return (
    <span className="vrh-col-titre" onClick={e => { e.stopPropagation(); setVal(label); setEditing(true); }} title={`${label} — cliquer pour renommer la colonne`}>
      {label}
    </span>
  );
}

function VivierCard({ candidat, colonnes, isDragging, isSelected, onDragStart, onDragEnd, onMove, onDelete, onEdit, onViewCV, onPropose, onQuickPatch, style, ancreTutoriel = null }) {
  const [showMenu, setShowMenu] = useState(false);
  const [editingNom, setEditingNom] = useState(false);
  const [editingTitre, setEditingTitre] = useState(false);
  const [tempNom, setTempNom] = useState('');
  const [tempTitre, setTempTitre] = useState('');
  const menuRef = useRef(null);
  const justDragged = useRef(false);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const initiales = ((candidat.prenom?.[0] || '') + (candidat.nom?.[0] || '')).toUpperCase() || '?';
  const couleurs = ['bg-blue-500','bg-indigo-500','bg-violet-500','bg-cyan-500','bg-emerald-500','bg-orange-500'];
  const couleur  = couleurs[(candidat.nom?.charCodeAt(0) || 0) % couleurs.length];

  const startEditNom = (e) => {
    e.stopPropagation();
    setTempNom(`${candidat.prenom || ''} ${candidat.nom || ''}`.trim());
    setEditingNom(true);
  };

  const commitNom = () => {
    const parts = tempNom.trim().split(/\s+/);
    const nom = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : parts[0].toUpperCase();
    const prenom = parts.length > 1 ? parts.slice(0, -1).map(p => p[0].toUpperCase() + p.slice(1)).join(' ') : '';
    onQuickPatch(candidat.id, { nom, prenom });
    setEditingNom(false);
  };

  const startEditTitre = (e) => {
    e.stopPropagation();
    setTempTitre(candidat.titre_vivier || '');
    setEditingTitre(true);
  };

  const commitTitre = () => {
    onQuickPatch(candidat.id, { titre_vivier: tempTitre.trim() });
    setEditingTitre(false);
  };

  // Ouvre la fiche — carte entièrement cliquable. Bloqué juste après un drag (clic fantôme) ou en édition.
  const onCardClick = () => {
    if (justDragged.current) return;
    if (editingNom || editingTitre) return;
    onEdit();
  };

  return (
    <div
      draggable
      onDragStart={e => { justDragged.current = true; onDragStart(e, candidat); }}
      onDragEnd={e => { onDragEnd(e); requestAnimationFrame(() => { justDragged.current = false; }); }}
      onClick={onCardClick}
      role="button" tabIndex={0}
      onKeyDown={e => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(); } }}
      className={`vrh-card ${isDragging ? 'vrh-card-drag' : ''} ${isSelected ? 'vrh-card-selected' : ''}`}
      style={style}
      {...(ancreTutoriel || {})}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="vrh-avatar">
            {initiales}
          </div>
          <div className="flex-1 min-w-0">
            {editingNom ? (
              <input
                autoFocus
                value={tempNom}
                onChange={e => setTempNom(e.target.value)}
                onBlur={commitNom}
                onKeyDown={e => { if (e.key === 'Enter') commitNom(); if (e.key === 'Escape') setEditingNom(false); }}
                onClick={e => e.stopPropagation()}
                className="w-full text-xs font-bold border border-indigo-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400"
              />
            ) : (
              <p className="vrh-name" onClick={e => { e.stopPropagation(); startEditNom(e); }} title="Cliquer pour modifier le nom">
                {candidat.prenom} {candidat.nom}
              </p>
            )}
            {editingTitre ? (
              <input
                autoFocus
                value={tempTitre}
                onChange={e => setTempTitre(e.target.value)}
                onBlur={commitTitre}
                onKeyDown={e => { if (e.key === 'Enter') commitTitre(); if (e.key === 'Escape') setEditingTitre(false); }}
                onClick={e => e.stopPropagation()}
                placeholder="Titre / spécialité..."
                className="w-full text-xs border border-indigo-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400 text-gray-600"
              />
            ) : (
              <p className="vrh-title" onClick={e => { e.stopPropagation(); startEditTitre(e); }} title="Cliquer pour modifier le titre">
                {candidat.titre_vivier || <span style={{ fontStyle: 'italic', color: 'var(--text-faint)' }}>+ titre</span>}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <div className="relative" ref={menuRef}>
            <button onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-xl shadow-lg z-[200] py-1 w-52" onClick={e => e.stopPropagation()}>
                <p className="text-xs text-gray-400 px-3 py-1 font-medium">Déplacer vers :</p>
                {colonnes.map(col => (
                  <button key={col.id} onClick={() => { onMove(col.id); setShowMenu(false); }}
                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 text-gray-700 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${col.color}`} />
                    {col.label}
                  </button>
                ))}
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button onClick={() => { onPropose && onPropose(candidat); setShowMenu(false); }}
                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-indigo-50 text-indigo-600 flex items-center gap-2">
                    <Send className="w-3 h-3" /> Proposer sur un besoin
                  </button>
                  <button onClick={() => { onDelete(); setShowMenu(false); }}
                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-red-50 text-red-500 flex items-center gap-2">
                    <Trash2 className="w-3 h-3" /> Retirer du vivier
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Infos — disposition handoff */}
      <div className="vrh-info">
        {(candidat.experience > 0 || candidat.localisation) && (
          <div className="vrh-meta-row">
            {candidat.experience > 0 && (
              <span className="it"><i className="bi bi-clock" /> {candidat.experience} ans</span>
            )}
            {candidat.localisation && (
              <span className="it"><i className="bi bi-geo-alt" /> {candidat.localisation}</span>
            )}
          </div>
        )}
        {candidat.disponibilite && (
          <span className="vrh-dispo"><i className="bi bi-check-lg" /> {candidat.disponibilite}</span>
        )}
        {(candidat.localisation || candidat.salaire_cible || candidat.lien_boond || candidat.mobilite) && (
          <div className="vrh-chips">
            {candidat.mobilite && <span className="vrh-chip">{candidat.mobilite}</span>}
            {candidat.salaire_cible && <span className="vrh-chip">{candidat.salaire_cible}</span>}
            {candidat.lien_boond && (
              <a href={candidat.lien_boond} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                className="vrh-chip" style={{ textDecoration: 'none' }}>BoondManager</a>
            )}
          </div>
        )}
        {candidat.note_vivier && (
          <p className="vrh-note">{candidat.note_vivier}</p>
        )}
        {candidat.resume_dernier_contact && (
          <p className="vrh-note">{candidat.resume_dernier_contact}</p>
        )}
        {candidat.whatsapp && (
          <div className="vrh-line">
            <i className="bi bi-telephone" /> {candidat.whatsapp}
            <a href={`https://wa.me/${candidat.whatsapp.replace(/[^0-9+]/g,'')}`} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()} className="vrh-wa">WA</a>
          </div>
        )}
        {candidat.cv_url && (
          <button onClick={e => { e.stopPropagation(); onViewCV && onViewCV(candidat); }} className="vrh-link">
            <i className="bi bi-file-earmark-text" /> Voir CV
          </button>
        )}
        {(candidat.created_by_name || candidat.date_dernier_contact) && (
          <div className="vrh-foot">
            <span>{candidat.created_by_name ? `Ajouté par ${candidat.created_by_name}` : ''}</span>
            <span>{candidat.date_dernier_contact ? new Date(candidat.date_dernier_contact).toLocaleDateString('fr-FR') : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Drawer fiche profil Vivier (lecture) ───────────────────────────────────
function VivierDrawer({ candidat, colonnes, candidats = [], onClose, onEdit, onPropose, onViewCV, onOpenCandidat, onSelectCandidate, onShortlist, onContact }) {
      if (!candidat) return null;
      const initiales = (((candidat.prenom || '')[0] || '') + ((candidat.nom || '')[0] || '')).toUpperCase() || '?';
      const col = colonnes.find(c => c.id === candidat.colonne_vivier);
      const phone = candidat.whatsapp || candidat.telephone || candidat.phone || candidat.mobile;
      const email = candidat.email || candidat.email_pro || candidat.mail;
      const position = Math.max(0, candidats.findIndex(c => String(c.id) === String(candidat.id)));
      const previous = candidats[position - 1];
      const next = candidats[position + 1];
      const skillsValue = candidat.competences_cles || candidat.competences || candidat.skills || candidat.tags || [];
      const skills = Array.isArray(skillsValue) ? skillsValue : String(skillsValue || '').split(/[,;|]/).map(item => item.trim()).filter(Boolean);
      const availability = candidat.disponibilite || candidat.statut_disponibilite || 'À confirmer';
      const status = candidat.statut || candidat.status || 'À qualifier';
      const activity = candidat.resume_dernier_contact || candidat.derniere_activite || candidat.last_activity || candidat.note_vivier || 'Aucune activité récente';
      const activityDate = candidat.date_dernier_contact || candidat.last_activity_at || candidat.updated_at;
      const contactHref = email ? 'mailto:' + email : phone ? 'tel:' + phone.replace(/[^0-9+]/g, '') : '';
      const isShortlisted = col?.id === 'coup_de_coeur' || /short|cœur|coeur/i.test(col?.label || '') || candidat.shortlisted === true;
      const Info = ({ icon, label, value }) => <div className="vd-info-line"><i className={'bi bi-' + icon} /><div><p className="vd-k">{label}</p><p className="vd-v">{value || '—'}</p></div></div>;

      return (
        <div className="vd-overlay" onClick={onClose}>
          <div className="vd-panel" onClick={e => e.stopPropagation()}>
            <div className="vd-preview-kicker"><span>APERÇU RAPIDE</span><strong>{position + 1} / {Math.max(candidats.length, 1)}</strong></div>
            <div className="vd-head">
              <div className="vd-av">{initiales}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="vd-name">{candidat.prenom} {candidat.nom}</h2>
                <p className="vd-sub">{candidat.titre_vivier || candidat.titre || 'Profil candidat'}</p>
                {candidat.match_score != null && <span className="vd-match"><i className="bi bi-intersect" /> {candidat.match_score}% match</span>}
              </div>
              <button onClick={onClose} className="vd-x" aria-label="Fermer l’aperçu"><X className="w-5 h-5" /></button>
            </div>
            <div className="vd-actions">
              {contactHref ? <a href={contactHref} onClick={() => onContact?.(candidat)} className="vd-btn vd-btn-primary"><i className="bi bi-send" /> Contacter</a> : <button onClick={() => onContact?.(candidat)} className="vd-btn vd-btn-primary"><i className="bi bi-send" /> Contacter</button>}
              <button onClick={() => onShortlist?.(candidat)} className={'vd-btn vd-btn-shortlist ' + (isShortlisted ? 'is-active' : '')}><i className={'bi ' + (isShortlisted ? 'bi-star-fill' : 'bi-star')} /> {isShortlisted ? 'Shortlistée' : 'Shortlist'}</button>
            </div>
            <div className="vd-body">
              <div className="vd-sect vd-status-section"><p className="vd-seclabel">Statut & disponibilité</p><div className="vd-status-card"><strong>{status}</strong><span><i className="bi bi-circle-fill" /> {availability}</span></div>{col && <span className="vd-colchip"><i className="bi bi-kanban" /> {col.label}</span>}</div>
              <div className="vd-sect"><p className="vd-seclabel">Coordonnées</p><div className="vd-contact-list">{email && <a href={'mailto:' + email} className="vd-contact-link"><i className="bi bi-envelope" /> {email}</a>}{phone && <a href={'tel:' + phone.replace(/[^0-9+]/g, '')} className="vd-contact-link"><i className="bi bi-telephone" /> {phone}</a>}{!email && !phone && <span className="vd-v">Coordonnées non renseignées</span>}</div></div>
              <div className="vd-sect"><p className="vd-seclabel">Compétences clés</p>{skills.length ? <div className="vd-tags">{skills.slice(0, 8).map((skill, index) => <span key={skill + index} className="vd-tag">{skill}</span>)}</div> : <span className="vd-v">À compléter dans la fiche candidat</span>}</div>
              <div className="vd-sect"><p className="vd-seclabel">Dernière activité</p><div className="vd-activity"><span className="vd-activity-dot" /><div><strong>{activity}</strong>{activityDate && <small>{new Date(activityDate).toLocaleDateString('fr-FR')}</small>}</div></div></div>
              {(candidat.experience || candidat.localisation || candidat.mobilite || candidat.salaire_cible) && <div className="vd-sect"><p className="vd-seclabel">Repères du profil</p><div className="vd-grid"><Info icon="clock" label="Expérience" value={candidat.experience ? candidat.experience + ' ans' : '—'} /><Info icon="geo-alt" label="Localisation" value={candidat.localisation} /><Info icon="compass" label="Mobilité" value={candidat.mobilite} /><Info icon="cash-stack" label="Salaire cible" value={candidat.salaire_cible} /></div></div>}
            </div>
            <div className="vd-navigation"><button onClick={() => previous && onSelectCandidate?.(previous)} disabled={!previous} aria-label="Candidat précédent"><i className="bi bi-chevron-left" /></button><span>Parcourir le vivier</span><button onClick={() => next && onSelectCandidate?.(next)} disabled={!next} aria-label="Candidat suivant"><i className="bi bi-chevron-right" /></button></div>
            <div className="vd-foot"><div className="vd-foot-primary">{onOpenCandidat && <button onClick={() => onOpenCandidat(candidat.id)} className="vd-btn vd-btn-primary"><i className="bi bi-arrows-angle-expand" /> Ouvrir la fiche complète</button>}{candidat.cv_url && <button onClick={() => onViewCV(candidat)} className="vd-btn"><i className="bi bi-file-earmark-text" /> CV</button>}</div><div className="vd-foot-secondary"><button onClick={() => onPropose(candidat)} className="vd-text-action">Ajouter à un besoin</button><button onClick={() => onEdit(candidat)} className="vd-text-action">Éditer</button><button onClick={onClose} className="vd-text-action">Fermer l’aperçu</button></div></div>
          </div>
        </div>
      );
    }

    // ─── Modal ajout/édition candidat vivier ────────────────────────────────────
function VivierModal({ candidat, colonnes, onSave, onClose }) {
  const [form, setForm] = useState({
    prenom: candidat?.prenom || '',
    nom: candidat?.nom || '',
    titre_vivier: candidat?.titre_vivier || '',
    experience: candidat?.experience || '',
    localisation: candidat?.localisation || '',
    disponibilite: candidat?.disponibilite || '',
    note_vivier: candidat?.note_vivier || '',
    colonne_vivier: candidat?.colonne_vivier || colonnes[0]?.id || '',
    cv_url: candidat?.cv_url || '',
    lien_boond: candidat?.lien_boond || '',
    whatsapp: candidat?.whatsapp || '',
    mobilite: candidat?.mobilite || '',
    salaire_cible: candidat?.salaire_cible || '',
    date_dernier_contact: candidat?.date_dernier_contact ? candidat.date_dernier_contact.split('T')[0] : '',
    resume_dernier_contact: candidat?.resume_dernier_contact || '',
    _cvFile: candidat?._cvFile || null,
  });

  return (
    <div className="vrm-overlay" onClick={onClose}>
      <div className="vrm-modal" onClick={e => e.stopPropagation()}>
        <div className="vrm-head">
          <h2 className="vrm-title">{candidat ? 'Modifier le profil' : 'Ajouter au vivier'}</h2>
          <button onClick={onClose} className="vrm-x"><X className="w-5 h-5" /></button>
        </div>
        <div className="vrm-body">
          <div className="vrm-grid">
            <div className="vrm-field">
              <label className="vrm-label">Prénom *</label>
              <input value={form.prenom} onChange={e => setForm(f => ({...f, prenom: e.target.value}))} className="vrm-input" />
            </div>
            <div className="vrm-field">
              <label className="vrm-label">Nom *</label>
              <input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} className="vrm-input" />
            </div>
          </div>
          <div className="vrm-field">
            <label className="vrm-label">Titre / Spécialité</label>
            <input value={form.titre_vivier} onChange={e => setForm(f => ({...f, titre_vivier: e.target.value}))}
              placeholder="ex: Business Analyste bancaire senior" className="vrm-input" />
          </div>
          <div className="vrm-grid">
            <div className="vrm-field">
              <label className="vrm-label">Expérience (ans)</label>
              <input type="number" value={form.experience} onChange={e => setForm(f => ({...f, experience: e.target.value}))} className="vrm-input" />
            </div>
            <div className="vrm-field">
              <label className="vrm-label">Localisation</label>
              <input value={form.localisation} onChange={e => setForm(f => ({...f, localisation: e.target.value}))} className="vrm-input" />
            </div>
          </div>
          <div className="vrm-grid">
            <div className="vrm-field">
              <label className="vrm-label">Disponibilité</label>
              <input value={form.disponibilite} onChange={e => setForm(f => ({...f, disponibilite: e.target.value}))}
                placeholder="ex: Immédiate, sous 1 mois..." className="vrm-input" />
            </div>
            <div className="vrm-field">
              <label className="vrm-label">Mobilité</label>
              <input value={form.mobilite} onChange={e => setForm(f => ({...f, mobilite: e.target.value}))}
                placeholder="ex: GE, VD, Full remote..." className="vrm-input" />
            </div>
          </div>
          <div className="vrm-field">
            <label className="vrm-label">Colonne vivier</label>
            <ErpiaSelect value={form.colonne_vivier} onChange={e => setForm(f => ({...f, colonne_vivier: e.target.value}))} className="vrm-input">
              {colonnes.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </ErpiaSelect>
          </div>
          <div className="vrm-field">
            <label className="vrm-label">Notes TAT (internes)</label>
            <textarea value={form.note_vivier} onChange={e => setForm(f => ({...f, note_vivier: e.target.value}))}
              placeholder="Points forts, contexte, disponibilité réelle..." rows={3} className="vrm-input" />
          </div>
          <div className="vrm-field">
            <label className="vrm-label">Lien BoondManager</label>
            <input value={form.lien_boond} onChange={e => setForm(f => ({...f, lien_boond: e.target.value}))}
              placeholder="https://app.boondmanager.com/..." className="vrm-input" />
          </div>
          <div className="vrm-grid">
            <div className="vrm-field">
              <label className="vrm-label">Téléphone</label>
              <input value={form.whatsapp} onChange={e => setForm(f => ({...f, whatsapp: e.target.value}))}
                placeholder="+41 79 000 00 00" className="vrm-input" />
              {form.whatsapp && (
                <a href={`https://wa.me/${form.whatsapp.replace(/[^0-9+]/g,'')}`} target="_blank" rel="noopener noreferrer" className="vrm-wa-btn">
                  <i className="bi bi-whatsapp" /> Ouvrir WhatsApp
                </a>
              )}
            </div>
            <div className="vrm-field">
              <label className="vrm-label">Salaire cible</label>
              <input value={form.salaire_cible} onChange={e => setForm(f => ({...f, salaire_cible: e.target.value}))}
                placeholder="ex: 120k CHF, 8500/mois" className="vrm-input" />
            </div>
          </div>
          <div className="vrm-grid">
            <div className="vrm-field">
              <label className="vrm-label">Date dernier contact</label>
              <input type="date" value={form.date_dernier_contact} onChange={e => setForm(f => ({...f, date_dernier_contact: e.target.value}))} className="vrm-input" />
            </div>
            <div className="vrm-field">
              <label className="vrm-label">Résumé du contact</label>
              <input value={form.resume_dernier_contact} onChange={e => setForm(f => ({...f, resume_dernier_contact: e.target.value}))}
                placeholder="Ex: Intéressé, disponible juin..." className="vrm-input" />
            </div>
          </div>
          <div className="vrm-field">
            <label className="vrm-label">CV (PDF)</label>
            <div className="flex items-center gap-2 flex-wrap">
              {form.cv_url ? (
                <button onClick={async () => {
                    if (!candidat?.id) {
                      // Pas encore d'id (création) → on ne peut pas passer par le proxy
                      // → afficher un msg au lieu d'ouvrir l'URL kDrive directement (sinon 401)
                      await alertErpia({ title: 'Profil non enregistré', message: 'Enregistrez d\'abord le profil pour pouvoir visualiser le CV.' });
                      return;
                    }
                    const h = { 'Authorization': `Bearer ${APIService.getToken()}` };
                    try {
                      const r = await fetch(`${BASE}/vivier/cv/${candidat.id}`, { headers: h });
                      if (!r.ok) {
                        let msg = `HTTP ${r.status}`;
                        try { const j = await r.json(); if (j.error) msg = j.error; } catch {}
                        await alertErpia({ title: 'Impossible d\'ouvrir le CV', message: 'Erreur chargement CV : ' + msg, tone: 'danger' });
                        return;
                      }
                      const blob = await r.blob();
                      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 5000);
                    } catch (e) {
                      await alertErpia({ title: 'Impossible d\'ouvrir le CV', message: 'Erreur chargement CV : ' + e.message, tone: 'danger' });
                    }
                  }}
                  className="vrm-cv-chip vrm-cv-ok">
                  <i className="bi bi-file-earmark-check" /> CV uploadé — Voir
                </button>
              ) : (
                <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Aucun CV</span>
              )}
              <label className="vrm-cv-chip vrm-cv-attach" style={{ cursor: 'pointer' }}>
                <i className={`bi ${form.cv_url ? 'bi-arrow-repeat' : 'bi-paperclip'}`} /> {form.cv_url ? 'Remplacer' : 'Joindre un CV'}
                <input type="file" accept=".pdf" className="hidden"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) setForm(f => ({...f, _cvFile: file}));
                  }} />
              </label>
              {form._cvFile && (
                <span style={{ fontSize: '12px', color: 'var(--text-2)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <i className="bi bi-file-earmark-text" /> {form._cvFile.name}
                </span>
              )}
            </div>
          </div>

        </div>
        <div className="vrm-foot">
          <button onClick={() => onSave(form)} disabled={!form.prenom.trim() || !form.nom.trim()} className="vrm-btn-primary">
            {candidat ? 'Enregistrer' : 'Ajouter au vivier'}
          </button>
          <button onClick={onClose} className="vrm-btn-ghost">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CV Frame (proxy kDrive) ─────────────────────────────────────────────────
function VivierCVFrame({ candidat, headers, BASE }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [errorStatus, setErrorStatus] = React.useState(null);
  const [retryKey, setRetryKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setBlobUrl(null); setLoading(true); setErrorStatus(null);
    if (!candidat.cv_url) { setLoading(false); return; }
    fetch(`${BASE}/vivier/cv/${candidat.id}`, { headers })
      .then(async r => {
        if (!r.ok) {
          let msg = `HTTP ${r.status}`;
          try { const j = await r.json(); if (j.error) msg = j.error; } catch {}
          throw new Error(msg);
        }
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setBlobUrl(createdUrl);
        setLoading(false);
      })
      .catch(err => { if (!cancelled) { setErrorStatus(String(err.message || err)); setLoading(false); } });
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [candidat.id, retryKey]);

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400 text-sm">Chargement du CV...</div>;
  if (!candidat.cv_url) return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
      <div className="text-5xl opacity-30"><i className="bi bi-file-earmark-text" /></div>
      <p>Aucun CV disponible pour ce profil</p>
    </div>
  );
  if (!blobUrl) return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 px-6">
      <div className="text-5xl opacity-30"><i className="bi bi-file-earmark-text" /></div>
      <p className="text-center">Impossible de charger le CV depuis kDrive</p>
      {errorStatus && <p className="text-xs text-red-300 font-mono bg-red-900/30 px-3 py-1.5 rounded max-w-md text-center">{errorStatus}</p>}
      <button onClick={() => setRetryKey(k => k + 1)}
        className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg">
        Réessayer
      </button>
      <p className="text-xs text-gray-500 mt-2">Si l'erreur persiste, vérifier les logs Railway pour la route /vivier/cv/{candidat.id}</p>
    </div>
  );
  return <iframe /* pas de sandbox : cf. DocumentViewer */src={blobUrl} className="w-full h-full border-0" title="CV" />;
}

// ─── Board principal ─────────────────────────────────────────────────────────
export default function VivierRH({ showNotification, onOpenInCandidats }) {
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  // Colonnes partagées — chargées depuis le backend (config globale)
  const [colonnes, setColonnes] = useState(DEFAULT_COLONNES);

  // Candidats vivier (stockés en backend dans la table candidats avec board='vivier')
  const [candidats, setCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCandidat, setEditingCandidat] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddCol, setShowAddCol] = useState(false);
  const [newColLabel, setNewColLabel] = useState('');

  const COLORS = ['bg-blue-600','bg-indigo-600','bg-violet-600','bg-purple-600','bg-cyan-600','bg-rose-500','bg-emerald-600','bg-orange-500','bg-slate-600'];
  const [newColColor, setNewColColor] = useState(COLORS[0]);
  const [colorPickerCol, setColorPickerCol] = useState(null);

  const saveColonnes = async (cols) => {
    setColonnes(cols);
    // Sauvegarder en backend — config partagée pour tous les utilisateurs
    try {
      await fetch(`${BASE}/config/vivier-colonnes`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ colonnes: cols })
      });
    } catch(e) {
      // Fallback localStorage si backend indispo
      localStorage.setItem('vivier_colonnes', JSON.stringify(cols));
    }
  };

  // L'annonce de chaque vivier (14.10 : « pourquoi il n'y a pas de JD sur le
  // vivier, sur le board ? ») : son texte et ses critères s'ouvrent depuis
  // l'en-tête de la colonne. Route absente : aucun bouton, rien de cassé.
  const [annoncesVivier, setAnnoncesVivier] = useState({});
  const [annonceOuverte, setAnnonceOuverte] = useState(null);
  const fetchAnnonces = async () => {
    try {
      const res = await fetch(`${BASE}/annonces`, { headers });
      if (!res.ok) return;
      const j = await res.json();
      const parVivier = {};
      for (const a of (j.annonces || [])) {
        if (a.cible !== 'vivier' || !a.vivier) continue;
        if (!parVivier[a.vivier] || (a.ouverte && !parVivier[a.vivier].ouverte)) parVivier[a.vivier] = a;
      }
      setAnnoncesVivier(parVivier);
    } catch { /* pas d'annonces : pas de bouton */ }
  };

  // Charger les colonnes partagées depuis le backend
  const fetchColonnes = async () => {
    try {
      const res = await fetch(`${BASE}/config/vivier-colonnes`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.colonnes && data.colonnes.length > 0) {
          // DB a des colonnes → tout le monde les utilise
          setColonnes(data.colonnes);
          localStorage.removeItem('vivier_colonnes'); // nettoyer localStorage
        } else {
          // DB vide → migrer depuis localStorage si dispo, sinon DEFAULT
          const local = localStorage.getItem('vivier_colonnes');
          if (local) {
            const parsed = JSON.parse(local);
            if (parsed && parsed.length > 0) {
              setColonnes(parsed);
              // Sauvegarder en DB pour que tout le monde les voie
              await fetch(`${BASE}/config/vivier-colonnes`, {
                method: 'PUT', headers,
                body: JSON.stringify({ colonnes: parsed })
              });
              localStorage.removeItem('vivier_colonnes');
              console.log('Colonnes migrées localStorage → DB');
            }
          }
        }
      }
    } catch(e) {
      try {
        const local = JSON.parse(localStorage.getItem('vivier_colonnes'));
        if (local && local.length > 0) setColonnes(local);
      } catch {}
    }
  };

  // Charger candidats vivier depuis le backend
  const fetchVivier = async () => {
    setLoading(true);
    try {
      const [vivierRes, usersRes] = await Promise.all([
        fetch(`${BASE}/vivier`, { headers }),
        fetch(`${BASE}/users`, { headers }).catch(() => null)
      ]);
      if (vivierRes.ok) {
        const data = await vivierRes.json();
        let list = Array.isArray(data) ? data : (data.data || []);
        // Enrichir avec le nom de l'auteur
        if (usersRes?.ok) {
          const usersData = await usersRes.json();
          const users = usersData.data || usersData.users || usersData || [];
          const userMap = Object.fromEntries(users.map(u => [u.id, u.username]));
          list = list.map(c => ({ ...c, created_by_name: userMap[c.created_by] || null }));
        }
        setCandidats(list);
      }
    } catch(e) {
      try {
        const local = JSON.parse(localStorage.getItem('vivier_candidats')) || [];
        setCandidats(local);
      } catch { setCandidats([]); }
    }
    setLoading(false);
  };

  useEffect(() => { fetchColonnes(); fetchVivier(); fetchAnnonces(); }, []);

  const saveCandidat = async (form) => {
    const { _cvFile, ...cleanForm } = form;
    let cv_url = cleanForm.cv_url || '';

    // Upload CV sur kDrive si nouveau fichier
    if (_cvFile) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            // Retirer le préfixe data URL si présent
            const clean = result.includes(',') ? result.split(',')[1] : result;
            resolve(clean);
          };
          reader.onerror = reject;
          reader.readAsDataURL(_cvFile);
        });
        console.log('📤 Upload CV vivier, taille base64:', base64.length);
        const uploadRes = await fetch(`${BASE}/vivier/upload-cv`, {
          method: 'POST', headers,
          body: JSON.stringify({ base64, fileName: _cvFile.name, mimeType: _cvFile.type || 'application/pdf' })
        });
        const uploadData = await uploadRes.json();
        console.log('📤 Upload CV response:', uploadData);
        if (uploadRes.ok) {
          cv_url = uploadData.url || uploadData.fileId || '';
          cleanForm.cv_file_id = uploadData.cv_file_id || uploadData.fileId || cv_url;
          console.log('CV URL stockée:', cv_url);
          if (!cv_url) {
            console.error('Upload OK mais URL vide:', uploadData);
          }
        } else {
          console.error('Upload CV échoué:', uploadData);
          showNotification('⚠️ CV non uploadé: ' + (uploadData?.error || 'erreur inconnue'), 'error');
        }
      } catch(e) {
        console.error('CV upload exception:', e.message);
        showNotification('⚠️ Erreur upload CV: ' + e.message, 'error');
      }
    }

    const payload = { ...cleanForm, cv_url, experience: parseInt(cleanForm.experience) || 0, board: 'vivier' };
    try {
      let res;
      if (editingCandidat?.id) {
        res = await fetch(`${BASE}/vivier/${editingCandidat.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${BASE}/vivier`, { method: 'POST', headers, body: JSON.stringify(payload) });
      }
      if (res.ok) {
        showNotification(editingCandidat ? 'Profil mis à jour' : 'Profil ajouté au vivier', 'success');
        setShowModal(false);
        setEditingCandidat(null);
        fetchVivier();
      } else {
        // Fallback localStorage
        const local = JSON.parse(localStorage.getItem('vivier_candidats')) || [];
        if (editingCandidat?.id) {
          const updated = local.map(c => c.id === editingCandidat.id ? {...payload, id: editingCandidat.id} : c);
          localStorage.setItem('vivier_candidats', JSON.stringify(updated));
          setCandidats(updated);
        } else {
          const newC = {...payload, id: Date.now()};
          const updated = [...local, newC];
          localStorage.setItem('vivier_candidats', JSON.stringify(updated));
          setCandidats(updated);
        }
        showNotification('Profil ajouté (local)', 'success');
        setShowModal(false);
        setEditingCandidat(null);
      }
    } catch(e) {
      showNotification('Erreur: ' + e.message, 'error');
    }
  };

  const deleteCandidat = async (id) => {
    if (!await confirmErpia({ message: 'Retirer ce profil du vivier ?' })) return;
    try {
      await fetch(`${BASE}/vivier/${id}`, { method: 'DELETE', headers });
    } catch(e) {}
    setCandidats(prev => {
      const updated = prev.filter(c => c.id !== id);
      localStorage.setItem('vivier_candidats', JSON.stringify(updated));
      return updated;
    });
    showNotification('Profil retiré du vivier', 'success');
  };

  const moveCandidat = async (id, newCol) => {
    setCandidats(prev => {
      const updated = prev.map(c => c.id === id ? {...c, colonne_vivier: newCol} : c);
      localStorage.setItem('vivier_candidats', JSON.stringify(updated));
      return updated;
    });
    try {
      await fetch(`${BASE}/vivier/${id}`, { method: 'PUT', headers, body: JSON.stringify({ colonne_vivier: newCol }) });
    } catch(e) {}
  };

  // Patch rapide d'un seul champ (inline edit)
  const quickPatch = async (id, patch) => {
    setCandidats(prev => prev.map(c => c.id === id ? {...c, ...patch} : c));
    try {
      await fetch(`${BASE}/vivier/${id}`, { method: 'PUT', headers, body: JSON.stringify(patch) });
    } catch(e) { showNotification('Erreur sauvegarde', 'error'); }
  };

  // Drag candidats
  const onDragStart = (e, candidat) => { setDragging(candidat); e.dataTransfer.effectAllowed = 'move'; };
  const onDragEnd   = () => { setDragging(null); setDragOver(null); setDragOverCol(null); };
  const onDragOver  = (e, colId) => { e.preventDefault(); setDragOver(colId); };
  const onDrop      = (e, colId) => {
    e.preventDefault();
    if (draggingCol) return; // laisser le drop colonne gérer
    if (dragging && dragging.colonne_vivier !== colId) moveCandidat(dragging.id, colId);
    setDragging(null); setDragOver(null);
  };

  // Drag colonnes
  const [draggingCol, setDraggingCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const onColDragStart = (e, colId) => {
    if (dragging) return; // candidat en cours de drag
    setDraggingCol(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };
  const onColDragOver = (e, colId) => {
    if (!draggingCol || draggingCol === colId) return;
    e.preventDefault(); e.stopPropagation();
    setDragOverCol(colId);
  };
  const onColDrop = (e, colId) => {
    e.stopPropagation();
    if (!draggingCol || draggingCol === colId) { setDraggingCol(null); setDragOverCol(null); return; }
    const from = colonnes.findIndex(c => c.id === draggingCol);
    const to   = colonnes.findIndex(c => c.id === colId);
    if (from !== -1 && to !== -1) {
      const reordered = [...colonnes];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      saveColonnes(reordered);
    }
    setDraggingCol(null); setDragOverCol(null);
  };

  const candidatsByCol = (colId) =>
    candidats.filter(c => (c.colonne_vivier || colonnes[0]?.id) === colId)
      .filter(c => !search || `${c.prenom} ${c.nom} ${c.titre_vivier}`.toLowerCase().includes(search.toLowerCase()));

  const previewCandidats = candidats.filter(c => !search || `${c.prenom} ${c.nom} ${c.titre_vivier}`.toLowerCase().includes(search.toLowerCase()));

  const [importing, setImporting] = React.useState(false);
  const [proposingCandidat, setProposingCandidat] = React.useState(null);
  const [besoins, setBesoins] = React.useState([]);
  const [viewingCV, setViewingCV] = React.useState(null);
  const [proposing, setProposing] = React.useState(false);
  const [besoinsFilter, setBesoinsFilter] = React.useState('');

  // Charger la liste des besoins pour le modal "proposer"
  const fetchBesoins = async () => {
    try {
      const res = await fetch(`${BASE}/besoins?limit=100`, { headers });
      const data = await res.json();
      setBesoins(Array.isArray(data) ? data : (data.data || []));
    } catch(e) {}
  };

  const proposeOnBesoin = async (candidat, besoinId) => {
    setProposing(true);
    try {
      const besoin = besoins.find(b => b.id === besoinId);
      const payload = {
        nom: `${candidat.prenom || ''} ${candidat.nom || ''}`.trim(),
        besoinId: besoinId,
        experience: candidat.experience || 0,
        localisation: candidat.localisation || '',
        disponibilite: candidat.disponibilite || '',
        commentaire: candidat.note_vivier || '',
        cv_url: candidat.cv_url || '',
        cvUrl: candidat.cv_url || '',  // backend attend cvUrl
        cvOriginalName: candidat.cv_url ? `CV_${(candidat.prenom || '')} ${(candidat.nom || '')}.pdf`.trim() : undefined,
      };
      const res = await fetch(`${BASE}/candidats`, {
        method: 'POST', headers,
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        showNotification(`${candidat.prenom} ${candidat.nom} proposé sur "${besoin?.titre}"`, 'success');
        setProposingCandidat(null);
      } else {
        const err = await res.json();
        showNotification('Erreur: ' + (err.error || 'inconnu'), 'error');
      }
    } catch(e) {
      showNotification('Erreur: ' + e.message, 'error');
    }
    setProposing(false);
  };

  const handleCVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset input
    setImporting(true);
    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call CV processor backend
      const res = await fetch(`${BASE}/cv-processor/process-url-base64`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ base64, mimeType: 'application/pdf', fileName: file.name })
      });

      let extracted = {};
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          const d = data.data;
          extracted = {
            prenom: (d.name || '').split(' ')[0] || '',
            nom: (d.name || '').split(' ').slice(1).join(' ') || '',
            titre_vivier: d.title || '',
            experience: d.annees_experience || 0,
            localisation: d.location || '',
            disponibilite: '',
            note_vivier: d.profile || '',
          };
        }
      }

      // Fallback: extract name from filename
      if (!extracted.nom) {
        const nomFichier = file.name.replace(/\.pdf$/i, '').replace(/_/g, ' ').trim();
        const parts = nomFichier.split(' ');
        extracted.prenom = parts[0] || '';
        extracted.nom = parts.slice(1).join(' ') || '';
      }

      // Uploader le CV sur kDrive pour pouvoir le consulter
      let cv_url = '';
      try {
        const formData = new FormData();
        formData.append('file', new Blob([await file.arrayBuffer()], { type: 'application/pdf' }), file.name);
        formData.append('candidatId', 'vivier_' + Date.now());
        formData.append('label', 'CV Vivier');
        // On stocke juste le nom de fichier, l'URL sera définie après création
        cv_url = ''; // sera mis à jour après sauvegarde
      } catch(e) {}

      showNotification('CV analysé — vérifiez et complétez', 'success');
      setEditingCandidat({ ...extracted, _cvFile: file, cv_url });
      setShowModal(true);
    } catch(e) {
      showNotification('Erreur import CV: ' + e.message, 'error');
    }
    setImporting(false);
  };

  const addColonne = () => {
    if (!newColLabel.trim()) return;
    const id = newColLabel.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now();
    saveColonnes([...colonnes, { id, label: newColLabel.trim(), color: newColColor }]);
    setNewColLabel('');
    setShowAddCol(false);
  };

  const totalCandidats = candidats.length;
  const profilsDisponibles = candidats.filter(c => Boolean(c.disponibilite)).length;
  const profilsVisibles = search.trim()
    ? candidats.filter(c => `${c.prenom} ${c.nom} ${c.titre_vivier}`.toLowerCase().includes(search.toLowerCase())).length
    : totalCandidats;

  return (
    <div className="vrh-root" style={{height: "100%"}}>
      <style>{VRH_STYLES}</style>
      {/* Header */}
      <div className="vrh-toolbar">
        <div className="vrh-toolbar-main">
          <Database className="w-5 h-5" style={{ color: 'var(--text-2)' }} />
          <div>
            <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Vivier RH<span style={{ color: 'var(--brand)' }}>.</span></h1>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-2)' }}>{loading ? 'Chargement des profils...' : `${profilsVisibles}/${totalCandidats} profils affichés`}</p>
          </div>
          <div className="vrh-summary" aria-label="Résumé du vivier">
            <span className="vrh-summary-item"><strong className="vrh-summary-value">{totalCandidats}</strong><span className="vrh-summary-label">qualifiés</span></span>
            <span className="vrh-summary-item"><strong className="vrh-summary-value">{profilsDisponibles}</strong><span className="vrh-summary-label">disponibles</span></span>
          </div>
        </div>
        <div className="vrh-toolbar-actions">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un profil..."
              className="vrh-input" style={{ paddingLeft: '34px', width: '208px' }}
              aria-label="Rechercher dans le vivier" data-testid="input-vivier-search" />
          </div>
          <BoutonAnnonce cible="vivier" className="vrh-btn-ghost" showNotification={showNotification} onVivierCree={() => { fetchColonnes(); fetchVivier(); fetchAnnonces(); }} />
          {annonceOuverte && (
            <AnnonceTiroir cible="vivier" annonceId={annonceOuverte} showNotification={showNotification}
              onClose={() => { setAnnonceOuverte(null); fetchAnnonces(); }}
              onModifiee={() => { fetchColonnes(); fetchAnnonces(); }}
              onVivierCree={() => { fetchColonnes(); fetchVivier(); fetchAnnonces(); }} />
          )}
          <button onClick={() => setShowAddCol(v => !v)} className="vrh-btn-ghost" data-testid="button-add-vivier-column">
            <Plus className="w-4 h-4" /> Colonne
          </button>
          <label className="vrh-btn-ghost" style={{ cursor: 'pointer' }} title="Importer depuis un CV PDF">
            {importing ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {importing ? 'Analyse...' : 'Importer CV'}
            <input type="file" accept=".pdf" className="hidden" onChange={handleCVImport} disabled={importing} />
          </label>
          <button onClick={() => { setEditingCandidat(null); setShowModal(true); }} className="vrh-btn-primary" data-testid="button-add-vivier-profile">
            <Plus className="w-4 h-4" /> Ajouter un profil
          </button>
        </div>
      </div>

      {/* Add column bar */}
      {showAddCol && (
        <div className="vrh-addcol">
          <input value={newColLabel} onChange={e => setNewColLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addColonne()}
            placeholder="Nom de la colonne..."
            className="vrh-input" style={{ width: '256px' }} />
          <div className="flex gap-1">
            {COLORS.map(c => (
              <button key={c} onClick={() => setNewColColor(c)}
                className={`w-5 h-5 rounded-full ${c} ${newColColor === c ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`} />
            ))}
          </div>
          <button onClick={addColonne} disabled={!newColLabel.trim()} className="vrh-btn-primary" style={{ opacity: newColLabel.trim() ? 1 : 0.4 }}>
            Ajouter
          </button>
          <button onClick={() => setShowAddCol(false)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Board */}
      <div className="vrh-board-wrap">
      {loading ? (
        <div className="vrh-loading-wrap" aria-label="Chargement du vivier">
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="vrh-loading-col" key={index}>
              <div className="vrh-skeleton vrh-skeleton-head" />
              <div className="vrh-skeleton" />
              <div className="vrh-skeleton" />
            </div>
          ))}
        </div>
      ) : (
      <div {...ancre('vivier-liste')} className="vrh-board">
        {colonnes.map((col, colIdx) => {
          const cards = candidatsByCol(col.id);
          const isOver = dragOver === col.id;
          const accent = accentHex(col.color);
          return (
            <div key={col.id}
              onDragOver={e => { onColDragOver(e, col.id); if (!draggingCol) onDragOver(e, col.id); }}
              onDrop={e => { if (draggingCol) onColDrop(e, col.id); else onDrop(e, col.id); }}
              onDragLeave={() => { setDragOver(null); setDragOverCol(null); }}
              className={`vrh-col ${isOver && !draggingCol ? 'vrh-col-drop-target' : ''}`}
              style={{ opacity: draggingCol === col.id ? 0.4 : 1 }}>

              {/* En-tête teinté discret (handoff) — draggable */}
              <div draggable onDragStart={e => onColDragStart(e, col.id)}
                style={{ ...colHeadStyle(accent), cursor: 'grab',
                  outline: (dragOverCol === col.id && draggingCol) || (isOver && !draggingCol) ? `2px solid ${hexA(accent, 0.55)}` : 'none' }}>
                <span className="vrh-col-sq" style={{ background: accent }} />
                <div className="flex items-center flex-1 min-w-0">
                  <ColLabelEditor
                    label={col.label}
                    onSave={(newLabel) => saveColonnes(colonnes.map(c => c.id === col.id ? {...c, label: newLabel} : c))}
                  />
                </div>
                <span style={colCountStyle(accent)}>{cards.length}</span>
                {annoncesVivier[col.id] && (
                  <button onClick={e => { e.stopPropagation(); setAnnonceOuverte(annoncesVivier[col.id].id); }}
                    className={`vrh-col-btn${annoncesVivier[col.id].a_valider ? ' vrh-col-btn--attente' : ''}`}
                    title={`Annonce du vivier : texte et critères${annoncesVivier[col.id].a_valider ? ' — à valider' : annoncesVivier[col.id].ouverte ? '' : ' (fermée)'}`}
                    aria-label={`Ouvrir l'annonce du vivier ${col.label}`}>
                    <i className="bi bi-megaphone" aria-hidden="true" />
                  </button>
                )}
                <button onClick={e => { e.stopPropagation(); setEditingCandidat({ colonne_vivier: col.id }); setShowModal(true); }}
                  className="vrh-col-btn" title="Ajouter un profil">
                  <Plus className="w-3.5 h-3.5" />
                </button>
                <div className="relative">
                  <button onClick={e => { e.stopPropagation(); setColorPickerCol(colorPickerCol === col.id ? null : col.id); }}
                    className="vrh-col-btn" title="Changer la couleur">
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                  {colorPickerCol === col.id && (
                    <div className="absolute right-0 top-8 flex flex-wrap gap-1 rounded-lg shadow-xl p-2 z-[200] w-36"
                      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                      onClick={e => e.stopPropagation()}>
                      {COLORS.map(c => (
                        <button key={c}
                          onClick={e => { e.stopPropagation(); saveColonnes(colonnes.map(cc => cc.id === col.id ? {...cc, color: c} : cc)); setColorPickerCol(null); }}
                          className={`w-5 h-5 rounded-full ${c} hover:scale-110 transition-transform ${col.color === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={async e => { e.stopPropagation(); if (await confirmErpia({ message: `Supprimer la colonne "${col.label}" ?` })) saveColonnes(colonnes.filter(c => c.id !== col.id)); }}
                  className="vrh-col-btn" title="Supprimer la colonne">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Cartes — neutres */}
              <div className="vrh-cards">
                {cards.map((c, index) => (
                  <VivierCard key={c.id} candidat={c} colonnes={colonnes}
                    ancreTutoriel={colIdx === 0 && index === 0 ? ancre('positionner') : null}
                    isDragging={dragging?.id === c.id}
                    isSelected={viewingProfile?.id === c.id}
                    onQuickPatch={quickPatch}
                    onDragStart={onDragStart} onDragEnd={onDragEnd}
                    onMove={(newCol) => moveCandidat(c.id, newCol)}
                    onDelete={() => deleteCandidat(c.id)}
                    onEdit={() => { setViewingProfile(c); }}
                    onViewCV={(cand) => setViewingCV(cand)}
                    onPropose={(cand) => { setProposingCandidat(cand); fetchBesoins(); }}
                    style={{ '--vrh-stagger': `${Math.min(index, 7) * 28}ms` }}
                  />
                ))}
                {cards.length === 0 && (
                  <div className="vrh-empty" style={{ borderColor: isOver ? hexA(accent, 0.5) : 'var(--border)', color: isOver ? 'var(--text-2)' : 'var(--text-faint)' }}>
                    {isOver ? 'Déposer ici' : 'Aucun profil'}
                    {!search && !isOver && <button onClick={e => { e.stopPropagation(); setEditingCandidat({ colonne_vivier: col.id }); setShowModal(true); }} className="vrh-empty-action">Ajouter un profil</button>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
      </div>

      {/* CV Viewer */}
      {viewingCV && (viewingCV.doc_id ?? viewingCV.document_id) != null && (viewingCV.candidate_id ?? viewingCV.candidat_id ?? viewingCV.candidateId) != null && (
        <CanonicalDocumentViewer candidateId={viewingCV.candidate_id ?? viewingCV.candidat_id ?? viewingCV.candidateId} docId={viewingCV.doc_id ?? viewingCV.document_id}
          label={`CV — ${viewingCV.prenom || ''} ${viewingCV.nom || ''}`.trim()}
          /* Cet aperçu n'ouvre QUE des CV — d'où cv sans condition : l'éditeur
             en ligne y a sa place comme ailleurs, un CV du vivier s'édite sans
             repasser par la fiche du candidat. */
          cv
          candidatNom={`${viewingCV.prenom || ''} ${viewingCV.nom || ''}`.trim()} showNotification={showNotification}
          onClose={() => setViewingCV(null)} />
      )}
      {viewingCV && !((viewingCV.doc_id ?? viewingCV.document_id) != null && (viewingCV.candidate_id ?? viewingCV.candidat_id ?? viewingCV.candidateId) != null) && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex flex-col z-[300]">
          <div className="flex items-center justify-between px-5 py-3 bg-gray-900 text-white flex-shrink-0">
            <span className="font-medium text-sm">{viewingCV.prenom} {viewingCV.nom} — CV</span>
            <div className="flex items-center gap-3">
              <a href={`${BASE}/vivier/cv/${viewingCV.id}`}
                onClick={async (e) => {
                  e.preventDefault();
                  const r = await fetch(`${BASE}/vivier/cv/${viewingCV.id}`, { headers });
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `CV_${viewingCV.prenom}_${viewingCV.nom}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                <Upload className="w-3.5 h-3.5" /> Télécharger
              </a>
              <button onClick={() => setViewingCV(null)} className="p-1.5 hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden bg-gray-800">
            <VivierCVFrame candidat={viewingCV} headers={headers} BASE={BASE} />
          </div>
        </div>
      )}

      {/* Propose sur un besoin */}
      {proposingCandidat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-base font-bold text-gray-800">Proposer sur un besoin</h2>
                <p className="text-xs text-gray-500">{proposingCandidat.prenom} {proposingCandidat.nom}</p>
              </div>
              <button onClick={() => setProposingCandidat(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 pt-3">
              <input
                autoFocus
                placeholder="Rechercher un besoin ou client..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  setBesoinsFilter(q);
                }}
              />
            </div>
            <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
              {besoins.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Chargement des besoins...</p>
              ) : besoins.filter(b => !besoinsFilter || `${b.titre} ${b.client}`.toLowerCase().includes(besoinsFilter)).map(b => (
                <button key={b.id} onClick={() => proposeOnBesoin(proposingCandidat, b.id)}
                  disabled={proposing}
                  className="w-full text-left p-3 border border-gray-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition disabled:opacity-50">
                  <p className="text-sm font-medium text-gray-800">{b.titre}</p>
                  <p className="text-xs text-gray-500">{b.client} · {b.responsable?.username || ''}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Drawer fiche profil (lecture) */}
      {viewingProfile && (
        <VivierDrawer
          candidat={viewingProfile}
          colonnes={colonnes}
          candidats={previewCandidats}
          onSelectCandidate={(candidate) => setViewingProfile(candidate)}
          onShortlist={(candidate) => {
            const shortlist = colonnes.find(c => c.id === 'coup_de_coeur') || colonnes.find(c => /short|coeur|cœur/i.test(c.label || ''));
            if (!shortlist) return showNotification('Aucune colonne shortlist disponible', 'error');
            if (candidate.colonne_vivier === shortlist.id) return;
            moveCandidat(candidate.id, shortlist.id);
            setViewingProfile(prev => prev?.id === candidate.id ? { ...prev, colonne_vivier: shortlist.id } : prev);
            showNotification('Profil ajouté à la shortlist', 'success');
          }}
          onContact={(candidate) => {
            if (!candidate.email && !candidate.email_pro && !candidate.mail && !candidate.whatsapp && !candidate.telephone && !candidate.phone && !candidate.mobile) showNotification('Aucune coordonnée disponible pour ce profil', 'error');
          }}
          onClose={() => setViewingProfile(null)}
          onEdit={(c) => { setViewingProfile(null); setEditingCandidat(c); setShowModal(true); }}
          onPropose={(c) => { setViewingProfile(null); setProposingCandidat(c); fetchBesoins(); }}
          onViewCV={(c) => setViewingCV(c)}
          onOpenCandidat={onOpenInCandidats ? (id) => { setViewingProfile(null); onOpenInCandidats(id); } : undefined}
        />
      )}

      {/* Modal ajout/édition */}
      {showModal && (
        <VivierModal
          candidat={editingCandidat}
          colonnes={colonnes}
          onSave={saveCandidat}
          onClose={() => { setShowModal(false); setEditingCandidat(null); }}
        />
      )}
    </div>
  );
}
